import React, { useState, useMemo } from "react";
import { useFormikContext } from "formik";
import { useCategories, addCategory } from "lib/api/category";
import { useSubCategories, addSubCategory } from "lib/api/subCategory";

import {
  HvGlobalActions,
  HvGrid,
  HvTextArea,
  HvDropdown,
  HvInput,
  ListValueProp,
  HvTypography,
} from "@hitachivantara/uikit-react-core";
import { Hidden, withStyles } from "@material-ui/core";
import { AddButton } from "components/common";
import { useTranslation } from "react-i18next";
import { IncidentType } from "lib/api";
import { useSnackbar } from "lib/provider/SnackbarProvider";

import useStyles from "./styles";
import { mutateData } from "./utils";
import AddEditCategoryDialog, {
  CategoryFormData,
  CategoryFormType,
} from "../AddEditCategoryDialog";

const IncidentTypeForm: React.FC = () => {
  const { t } = useTranslation("addEditIncidentType");
  const { enqueueSnackbar } = useSnackbar();

  const {
    values,
    errors,
    status: { serverSideErrors = {} },
    touched,
    setFieldValue,
    setFieldTouched,
    setStatus,
  } = useFormikContext<Partial<IncidentType>>();

  const classes = useStyles();

  const [categoryOpen, setCategoryOpen] = useState(false);
  const [subcategoryOpen, setSubcategoryOpen] = useState(false);

  const [addDialogIsOpen, setAddDialogIsOpen] = useState(false);
  const [dialogType, setDialogType] = useState<CategoryFormType>("category");
  const dropdownStyles = useMemo(
    () => ({
      dropdownListContainer: {
        maxHeight: 300,
        overflow: "auto",
        padding: 5,
      },
    }),
    []
  );

  const ScrollDropdown = useMemo(
    () => withStyles(dropdownStyles)(HvDropdown),
    [dropdownStyles]
  );

  const { data: categoryData, mutate: mutateCat } = useCategories(undefined, {
    revalidateOnFocus: false,
  });
  const { data: subCategoryData, mutate: mutateSubcat } = useSubCategories(
    undefined,
    { revalidateOnFocus: false }
  );

  const categoryValues = useMemo(() => {
    const retArray: ListValueProp[] = [];
    categoryData?.data.forEach((r) => {
      retArray.push({
        id: r.pk_cat_id.toString(),
        label: r.cat_name,
        selected: r.pk_cat_id === values.fk_cat_id,
      });
    });
    return retArray;
  }, [categoryData?.data, values.fk_cat_id]);

  const subcategoryValues = useMemo(() => {
    const retArray: ListValueProp[] = [];
    subCategoryData?.data.forEach((r) => {
      retArray.push({
        id: r.pk_sub_cat_id.toString(),
        label: r.sub_cat_name,
        selected: r.pk_sub_cat_id === values.fk_sub_cat_id,
      });
    });
    return retArray;
  }, [subCategoryData?.data, values.fk_sub_cat_id]);

  const clearServerSideErrors = () => {
    setStatus({});
  };

  const parseStatus = (field: keyof Partial<IncidentType>) =>
    serverSideErrors[field] || (errors[field] && touched[field])
      ? "invalid"
      : "valid";

  const parseStatusMessage = (field: keyof Partial<IncidentType>) => {
    if (serverSideErrors[field]) {
      return serverSideErrors[field];
    }

    return errors[field] && touched[field] ? errors[field] : "";
  };

  const setValueOnForm = (
    selection: string | number | undefined,
    property: string
  ) => {
    setFieldValue(property, selection);
    clearServerSideErrors();
  };

  const onSave = (data: CategoryFormData, type: CategoryFormType) => {
    return mutateData(
      data,
      type,
      mutateCat,
      mutateSubcat,
      addCategory,
      addSubCategory
    );
  };

  const openAddCategoryDialog = () => {
    setAddDialogIsOpen(true);
    setDialogType("category");
  };

  const openAddSubcategoryDialog = () => {
    setAddDialogIsOpen(true);
    setDialogType("subcategory");
  };

  const onDialogClose = (success: boolean, message?: string) => {
    if (message != null) {
      enqueueSnackbar(message, { variant: success ? "success" : "error" });
    }
    setAddDialogIsOpen(false);
  };

  return (
    <HvGrid className={classes.root} container>
      <AddEditCategoryDialog
        isOpen={addDialogIsOpen}
        mode="add"
        onSave={(data: CategoryFormData) => onSave(data, dialogType)}
        onClose={onDialogClose}
        type={dialogType}
      />
      <HvGrid item sm={12}>
        <HvGlobalActions
          title={t("sections.details.title")}
          variant="section"
        />
        <HvTypography variant="normalText">
          {t("sections.details.description")}
        </HvTypography>
      </HvGrid>

      <HvGrid container className={classes.fields} item xs={12} lg={9}>
        <HvGrid item xs={12} md={6}>
          <HvInput
            name="name"
            className={classes.nameInput}
            label={t("details.name")}
            placeholder={t("details.namePlaceholder")}
            required
            value={values.inc_type_name}
            status={parseStatus("inc_type_name")}
            statusMessage={parseStatusMessage("inc_type_name")}
            onBlur={() => setFieldTouched("inc_type_name")}
            onChange={(evt, value) => {
              setValueOnForm(value, "inc_type_name");
            }}
          />
        </HvGrid>
        <Hidden smDown>
          <HvGrid item md={6} role="presentation" />
        </Hidden>
        <HvGrid item xs={12} md={6}>
          <ScrollDropdown
            name="category"
            expanded={categoryOpen}
            className={classes.dropdownTopSection}
            values={categoryValues}
            status={parseStatus("fk_cat_id")}
            statusMessage={parseStatusMessage("fk_cat_id")}
            label={t("details.category")}
            placeholder={t("details.categoryPlaceholder")}
            showSearch={categoryValues?.length > 10}
            description={
              <AddButton onClick={openAddCategoryDialog}>
                {t("details.addCategory")}
              </AddButton>
            }
            required
            onChange={(selection) => {
              const selectedValue = selection as ListValueProp;
              if (!selectedValue) {
                setValueOnForm(undefined, "fk_cat_id");
                return;
              }
              setValueOnForm(Number(selectedValue?.id), "fk_cat_id");
            }}
            onToggle={(event, open) => {
              setFieldTouched("fk_cat_id", true, false);
              setSubcategoryOpen(false);
              setCategoryOpen(open);
            }}
          />
        </HvGrid>
        <HvGrid item xs={12} md={6}>
          <ScrollDropdown
            name="subcategory"
            expanded={subcategoryOpen}
            className={classes.dropdownTopSection}
            values={subcategoryValues}
            status={parseStatus("fk_sub_cat_id")}
            statusMessage={parseStatusMessage("fk_sub_cat_id")}
            label={t("details.subcategory")}
            placeholder={t("details.subcategoryPlaceholder")}
            description={
              <AddButton onClick={openAddSubcategoryDialog}>
                {t("details.addSubcategory")}
              </AddButton>
            }
            showSearch={subcategoryValues?.length > 10}
            required
            onChange={(selection) => {
              const selectedValue = selection as ListValueProp;
              if (!selectedValue) {
                setValueOnForm(undefined, "fk_sub_cat_id");
                return;
              }
              setValueOnForm(Number(selectedValue?.id), "fk_sub_cat_id");
            }}
            onToggle={(event, open) => {
              setFieldTouched("fk_sub_cat_id", true, false);
              setCategoryOpen(false);
              setSubcategoryOpen(open);
            }}
          />
        </HvGrid>
        <HvGrid item xs={12}>
          <HvTextArea
            name="description"
            classes={{
              input: classes.textareaInput,
            }}
            value={values.inc_type_desc}
            status={parseStatus("inc_type_desc")}
            statusMessage={parseStatusMessage("inc_type_desc")}
            onChange={(evt, value) => {
              setValueOnForm(value, "inc_type_desc");
            }}
            label={t("details.description")}
            placeholder={t("details.descriptionPlaceholder")}
            rows={5}
          />
        </HvGrid>
      </HvGrid>
    </HvGrid>
  );
};

export default IncidentTypeForm;
